import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { useRouter } from 'next/router';

/**
 * Route pattern → zone name (e.g. `"/account/signin": "monetization"`), generated per shell at
 * build time by `workflow/scripts/build-page-remote-map.js` into `src/config/zone-map.json`.
 * Patterns come in two syntaxes: regional-config paths (`/movies/video/${movieId}/`) and Next.js
 * page routes (`/movies/[id]`, `/live-tv/[[...params]]`).
 */
export type ZoneMap = Record<string, string>;

const ZoneMapContext = createContext<ZoneMap | undefined>(undefined);

export type HardNavigationReason = 'cross-zone' | 'legacy';

export type SpeculationMode = 'off' | 'prefetch' | 'prerender';

/**
 * Speculation Rules (Chrome 121+; other browsers ignore the script type) make the hard
 * navigation near-instant: hovering a marked anchor prefetches the target document, and — in
 * `prerender` mode — pointer-down fully prerenders it, so activation is an instant swap.
 * Document rules key off the `data-zone-nav` attribute that RegionAwareLink's hard-nav branch
 * stamps on its anchors. Prerender is restricted to cross-zone (Terranova) pages: prerendering
 * executes the target page's JS, and legacy pages' tracking is not prerender-aware. Same reason
 * prerender is opt-in: our own analytics must gate on `document.prerendering` first.
 */
const speculationRules = (mode: SpeculationMode) =>
    JSON.stringify({
        prefetch: [{ where: { selector_matches: 'a[data-zone-nav]' }, eagerness: 'moderate' }],
        ...(mode === 'prerender' && {
            prerender: [
                {
                    where: { selector_matches: "a[data-zone-nav='cross-zone']" },
                    eagerness: 'conservative',
                },
            ],
        }),
    });

/**
 * Enables zone-aware navigation (Next.js Multi-Zones). Mount in the shell `_app` with the
 * generated zone map; when absent, `useZone().shouldHardNavigate` is always false and links
 * behave exactly as before — provider presence is the rollout switch.
 */
export const ZoneMapProvider = ({
    zoneMap,
    speculation = 'prefetch',
    children,
}: Readonly<{ zoneMap: ZoneMap; speculation?: SpeculationMode; children: ReactNode }>) => (
    <ZoneMapContext.Provider value={zoneMap}>
        {speculation !== 'off' && (
            <script
                type="speculationrules"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: speculationRules(speculation) }}
            />
        )}
        {children}
    </ZoneMapContext.Provider>
);

const isParamSegment = (segment: string) => /^(\$\{.+\}|\[.+\])$/.test(segment);
const isCatchAllSegment = (segment: string) => /^\[\[?\.\.\..+\]\]?$/.test(segment);

const toSegments = (path: string) => path.split(/[?#]/)[0].split('/').filter(Boolean);

// Note: params are assumed to span whole segments (true for every pattern in the map today);
// support partial-segment params (`/x-${id}/`) only if a blueprint ever defines one.
const matchesPattern = (patternSegments: string[], pathSegments: string[]): boolean => {
    for (let i = 0; i < patternSegments.length; i += 1) {
        const pattern = patternSegments[i];
        if (isCatchAllSegment(pattern)) return true;
        if (pathSegments[i] === undefined) return false;
        if (!isParamSegment(pattern) && pattern !== pathSegments[i]) return false;
    }
    return pathSegments.length === patternSegments.length;
};

type CompiledZoneMap = Array<{ segments: string[]; zone: string }>;

export const zoneForPath = (compiled: CompiledZoneMap, path: string): string | undefined =>
    compiled.find(({ segments }) => matchesPattern(segments, toSegments(path)))?.zone;

/**
 * Zone classification for Multi-Zones navigation. `hardNavigationReason(href)` is set when the
 * destination must be a full page load instead of an SPA route push:
 *   - `'cross-zone'`: the destination belongs to a different zone than the current page
 *     (standard multi-zone behavior), or
 *   - `'legacy'`: the destination matches no Terranova route — a page nginx must route.
 * Non-path hrefs (absolute URLs, hashes, empty) and missing provider both return undefined,
 * leaving the caller's existing behavior untouched. `shouldHardNavigate` is the boolean view.
 */
export const useZone = () => {
    const zoneMap = useContext(ZoneMapContext);
    // RegionAwareLink, the only consumer, already calls useRouter() unconditionally.
    const currentPathname: string | undefined = useRouter().pathname;

    const compiled = useMemo<CompiledZoneMap | undefined>(
        () =>
            zoneMap &&
            Object.entries(zoneMap).map(([pattern, zone]) => ({
                segments: toSegments(pattern),
                zone,
            })),
        [zoneMap],
    );

    // Current zone from `router.pathname` — the Next page route pattern, which the map's
    // pages-walk entries contain verbatim, so it classifies without region stripping.
    const hardNavigationReason = useCallback(
        (canonicalHref: string): HardNavigationReason | undefined => {
            if (!compiled || !canonicalHref.startsWith('/') || canonicalHref.startsWith('//')) {
                return undefined;
            }
            const destinationZone = zoneForPath(compiled, canonicalHref);
            if (!destinationZone) {
                return 'legacy'; // not a Terranova route → must go through nginx
            }
            const currentZone = currentPathname && zoneForPath(compiled, currentPathname);
            // Unknown current zone means a map coverage gap on this page — fail soft (SPA nav).
            return !!currentZone && destinationZone !== currentZone ? 'cross-zone' : undefined;
        },
        [compiled, currentPathname],
    );

    const shouldHardNavigate = useCallback(
        (canonicalHref: string): boolean => !!hardNavigationReason(canonicalHref),
        [hardNavigationReason],
    );

    return { hardNavigationReason, shouldHardNavigate };
};
